import {
  Content,
  Group,
  Item,
  Label,
  Portal,
  Root,
  Separator,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { type ComponentProps, forwardRef, type ReactNode } from 'react';

import styles from './Menu.module.css';

export interface MenuProps {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Dropdown menu built on Radix. Compose: `Menu` > `MenuButton` (the trigger) +
 *  `MenuList` > `MenuItem`/`MenuGroup`/`MenuDivider`. */
export const Menu = ({ children, ...props }: MenuProps) => (
  <Root {...props}>{children}</Root>
);

export interface MenuButtonProps {
  /** A single trigger element (e.g. a Button/IconButton that forwards a ref). */
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}

/** The element that opens the menu. Wraps a single ref-forwarding child. */
export const MenuButton = ({
  children,
  asChild = true,
  className,
}: MenuButtonProps) => (
  <Trigger asChild={asChild} className={className}>
    {children}
  </Trigger>
);

export interface MenuListProps
  extends Omit<ComponentProps<typeof Content>, 'asChild'> {
  children: ReactNode;
  portal?: boolean;
  /** Min width of the menu surface; overrides the default 10rem floor. */
  minWidth?: number | string;
}

export const MenuList = ({
  children,
  className,
  portal = true,
  minWidth,
  style,
  ...props
}: MenuListProps) => {
  const content = (
    <Content
      className={classNames(styles.content, className)}
      sideOffset={4}
      align="start"
      style={minWidth !== undefined ? { ...style, minWidth } : style}
      {...props}
    >
      {children}
    </Content>
  );

  return portal ? <Portal>{content}</Portal> : content;
};

export interface MenuItemProps
  extends Omit<
    ComponentProps<typeof Item>,
    'onSelect' | 'asChild' | 'onClick'
  > {
  /** Fires on click and keyboard select. Receives the Radix select event, so
   *  callers can `event.preventDefault()` to keep the menu open. */
  onClick?: (event: Event) => void;
  icon?: ReactNode;
}

/** A menu action. forwardRef + prop spread so it composes as a Tooltip/Trigger
 *  `asChild` target. */
export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  function MenuItem(
    { children, onClick, disabled, icon, className, ...rest },
    ref
  ) {
    return (
      <Item
        ref={ref}
        className={classNames(styles.item, className)}
        disabled={disabled}
        onSelect={onClick}
        {...rest}
      >
        {icon ? (
          <span className={styles.itemIcon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {children}
      </Item>
    );
  }
);

export interface MenuGroupProps {
  children: ReactNode;
  label?: ReactNode;
  className?: string;
}

export const MenuGroup = ({ children, label, className }: MenuGroupProps) => (
  <Group className={className}>
    {label ? <Label className={styles.groupLabel}>{label}</Label> : null}
    {children}
  </Group>
);

export const MenuDivider = ({ className }: { className?: string }) => (
  <Separator className={classNames(styles.divider, className)} />
);
